import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  MultiSelect,
  MultiSelectChangeEvent,
  ListItemProps,
} from '@progress/kendo-react-dropdowns';
import clsx from 'clsx';

import {
  fetchStores,
  fetchLanesBySite,
  fetchDivisions,
  setPanelBarVisibility,
} from '../../store/Reports/ReportsActions';
import { Divider, Grid } from '@material-ui/core';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { ReportNodeInfo } from '../../Interfaces/Reports/ReportNodeInfo';
import enableIcon from '../../assets/images/arrow-right.png';
import disableIcon from '../../assets/images/gray-arrow.png';
import { ReportCategory } from '../../Interfaces/Reports/ReportCategory';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Error } from '@progress/kendo-react-labels';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { saveReportFilter } from '../../store/UserSettings/UserSettingsActions';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { getDateRange } from '../../utils/utils';
import { saveNotificationsReportFilter } from '../../store/Notifications/NotificationsActions';
import DynamicDropDownList from '../DynamicDropDownList';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { ReportExportResult } from '../../Interfaces/Reports/ReportExportResult';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
    },
    margin: {
      margin: theme.spacing(1),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      height: '50px',
      margin: '20px',
      width: '50px',
    },
    disable: {
      backgroundColor: '#e9ecef',
    },
    enable: {
      backgroundColor: '#FFFFFF',
    },
    dropDownLabelGrid: {
      width: '270px',
    },
    dateRangeDropDownLabelGrid: {
      width: '270px',
    },
    box: {
      display: 'flex',
      width: 500,
      height: 300,
      flexDirection: 'column',
      borderRadius: 5,
      backgroundColor: '#fff',
      padding: 10,
    },
    header: {
      flex: 0.1,
      display: 'flex',
      flexDirection: 'row',
    },
    title: {
      alignItems: 'center',
      justifyContent: 'center',
      color: '#23496B',
      display: 'flex',
      flex: 0.3,
    },
    headerSpace: {
      display: 'flex',
      flex: 0.7,
      alignItems: 'center',
      color: '#23496B',
      justifyContent: 'flex-end',
      marginRight: 20,
    },
    dateRangeModal: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.shape.borderRadius,
      height: '500px',
    },
    dateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      margin: theme.spacing(2),
    },
    headerRowElement: {
      margin: theme.spacing(0.5),
    },
    bottomContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: '100px',
    },
    actionButton: {
      backgroundColor: '#153654',
      width: '150px',
      color: '#fff',
      '&:hover': {
        background: '#153654',
        color: '#fff',
      },
    },
    selectedDateRange: {
      marginTop: '20px',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '15px',
      width: 260,
    },
    endDateRange: {
      marginTop: '-15px',
      fontSize: '15px',
      width: 260,
    },
    mainContainer: { display: 'flex', flexDirection: 'row' },
    reportFilterContainer: { width: '100%' },
    notificationText: {
      fontSize: '1.5rem',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
  }),
);

interface Props {
  setIsReportsPanelBarVisible: (bool: boolean) => void;
  isReportsPage: boolean;
  returnToReportsList?: () => void;
}

const ReportsFilter: React.FC<Props> = ({
  setIsReportsPanelBarVisible,
  isReportsPage,
  returnToReportsList,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const stores: string[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.stores,
  );

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.divisions,
  );

  const reportNodeInfo: ReportNodeInfo[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportNodeInfo,
  );

  const reportCategories: ReportCategory[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.categories,
  );

  const reportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.reportFilter,
  );

  const notificationsReportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.notificationsReducer.reportFilter,
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const panelBarIsVisible: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.panelBarVisible,
  );

  const isFetchingDivisions: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.isFetchingDivisions,
  );

  const isFetchingStores: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.isFetchingStores,
  );

  const isFetchingReportNodeInfo: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.isFetchingReportNodeInfo,
  );

  const reportExportResult: ReportExportResult = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportExportResult,
  );

  const isExportResultReceived: boolean = useSelector(
    (state: ApplicationState) => state.reportsReducer.isExportResultReceived,
  );

  const dateRangeOption = [
    GetString('TODAY'),
    GetString('YESTERDAY'),
    GetString('LAST_SEVEN_DAYS'),
    GetString('OTHER'),
  ];

  const [selectedDivision, setSelectedDivision] = React.useState<string>();

  const [selectedStores, setSelectedStores] = React.useState<string[]>([]);

  const [selectedDateRange, setSelectedDateRange] = React.useState<string>(
    dateRangeOption[0],
  );

  const [laneNumbers, setLaneNumbers] = React.useState<string[]>([]);

  const [storeList, setStoreList] = React.useState<string[]>([]);

  const [selectedLanes, setSelectedLanes] = React.useState<string[]>([]);

  const [modalVisible, setModalVisible] = React.useState<boolean>(false);

  const [dateRangeSelection, setDateRangeSelection] = React.useState<Date>();

  const [resetDateRangeOption, setResetDateRangeOptions] = React.useState<
    string[]
  >([dateRangeOption[0]]);

  const [dateRangeSelectionVisible, setDateRangeSelectionVisible] =
    React.useState<boolean>(false);

  const [startDateRange, setStartDateRange] = React.useState<Date>(new Date());

  const [endDateRange, setEndDateRange] = React.useState<Date>(new Date());

  const [isStartDateRangeChange, setIsStartDateRangeChange] =
    React.useState<boolean>(false);

  const [isEndDateRangeChange, setIsEndDateRangeChange] =
    React.useState<boolean>(false);

  const [selectedStartDateTime, setSelectedStartDateTime] =
    React.useState<Date>(new Date());

  const [selectedEndDateTime, setSelectedEndDateTime] = React.useState<Date>(
    new Date(),
  );

  const [laneSelectionChange, setLaneSelectionChange] = React.useState<
    boolean | undefined
  >();

  const [errorMsg, setErrorMsg] = React.useState(false);

  const [displayNotification, setNotificationVisible] =
    React.useState<boolean>(false);

  const startDateLabel = GetString('START_DATE_LABEL');
  const endDateLabel = GetString('END_DATE_LABEL');
  const START_DATE_ERRORMSG = GetString('START_DATE_ERRORMSG');
  const END_DATE_ERRORMSG = GetString('END_DATE_ERRORMSG');
  const selectAll = GetString('SELECT_ALL');
  const itemSelected = GetString('ITEM_SELECTED');
  const itemsSelected = GetString('ITEMS_SELECTED');
  const storeSelected = GetString('STORE_SELECTED');
  const storesSelected = GetString('STORES_SELECTED');
  const laneSelected = GetString('LANE_SELECTED');
  const lanesSelected = GetString('LANES_SELECTED');


  const refreshDateRange = (
    newReportFilter: ReportFilter,
    rangeOption: ApiEnums.DateRangeOptions,
  ) => {
    let range = getDateRange(rangeOption);
    if (range !== undefined) {
      newReportFilter.StartDateTime = range[0];
      newReportFilter.EndDateTime = range[1];
    }
  };

  React.useEffect(() => {
    if (isExportResultReceived) {
      setNotificationVisible(true);
      setTimeout(() => {
        setNotificationVisible(false);
      }, 3000);
    }
  }, [isExportResultReceived]);

  const onStoreChange = (event: MultiSelectChangeEvent) => {
    clearLaneFilterData();

    const currentSelectAll = selectedStores.some(i => i === selectAll);

    const nextSelectAll = event.value.some((i: string) => i === selectAll);

    let newValue = event.value;
    const currentCount = selectedStores.length;
    const nextCount = newValue.length;

    if (
      nextCount > currentCount &&
      !currentSelectAll &&
      !nextSelectAll &&
      storeList.length - 1 === nextCount
    ) {
      newValue = storeList;
    } else if (
      nextCount < currentCount &&
      currentCount === stores.length &&
      currentSelectAll &&
      nextSelectAll
    ) {
      newValue = selectedStores.filter(v => v !== selectAll);
    } else if (!currentSelectAll && nextSelectAll) {
      newValue = storeList;
    } else if (currentSelectAll && !nextSelectAll) {
      newValue = [];
    }

    const filteredValue = newValue.filter((e: string) => e !== selectAll);

    if (filteredValue.length == 1) {
      fetchLanesBySite(dispatch, filteredValue[0]);
      setPanelBarVisibility(dispatch, true);
    } else if (filteredValue.length > 1) {
      setPanelBarVisibility(dispatch, true);
    } else {
      setPanelBarVisibility(dispatch, false);
    }
    setSelectedStores(newValue);

    let newReportFilter: ReportFilter = isReportsPage
      ? JSON.parse(JSON.stringify(reportFilter))
      : JSON.parse(JSON.stringify(notificationsReportFilter));
    newReportFilter.SiteNumbers = newValue.filter(
      (e: string) => e !== selectAll,
    );

    // Refresh start and end dates (since the actual date may have changed since the UI was last refreshed)
    refreshDateRange(newReportFilter, newReportFilter.DateRange);

    newReportFilter.LaneNumbers = [];
    if (isReportsPage) {
      saveReportFilter(dispatch, newReportFilter);
    } else {
      saveNotificationsReportFilter(dispatch, newReportFilter);
    }

    setDateRangeSelectionVisible(false);
  };

  const onLaneChange = (event: MultiSelectChangeEvent) => {
    const currentSelectAll = selectedLanes.some(i => i === selectAll);
    const nextSelectAll = event.value.some((i: string) => i === selectAll);

    let newValue = event.value;
    const currentCount = selectedLanes.length;
    const nextCount = newValue.length;

    if (
      nextCount > currentCount &&
      !currentSelectAll &&
      !nextSelectAll &&
      laneNumbers.length - 1 === nextCount
    ) {
      newValue = laneNumbers;
    } else if (
      nextCount < currentCount &&
      currentCount === laneNumbers.length &&
      currentSelectAll &&
      nextSelectAll
    ) {
      newValue = selectedLanes.filter(v => v !== selectAll);
    } else if (!currentSelectAll && nextSelectAll) {
      newValue = laneNumbers;
    } else if (currentSelectAll && !nextSelectAll) {
      newValue = [];
    }

    setSelectedLanes(newValue);
    setDateRangeSelectionVisible(false);

    let newReportFilter: ReportFilter = isReportsPage
      ? JSON.parse(JSON.stringify(reportFilter))
      : JSON.parse(JSON.stringify(notificationsReportFilter));
    newReportFilter.LaneNumbers = newValue.filter(
      (e: string) => e !== selectAll,
    );

    // Refresh start and end dates (since the actual date may have changed since the UI was last refreshed)
    refreshDateRange(newReportFilter, newReportFilter.DateRange);

    if (isReportsPage) {
      saveReportFilter(dispatch, newReportFilter);
    } else {
      saveNotificationsReportFilter(dispatch, newReportFilter);
    }
    setLaneSelectionChange(!laneSelectionChange);
  };

  const handleDivisionChange = (event: DropDownListChangeEvent) => {
    setSelectedStores([]);
    clearLaneFilterData();
    setPanelBarVisibility(dispatch, false);
    setSelectedDivision(event.value);
    fetchStores(dispatch, event.value);
    setDateRangeSelectionVisible(false);

    let newReportFilter: ReportFilter = isReportsPage
      ? JSON.parse(JSON.stringify(reportFilter))
      : JSON.parse(JSON.stringify(notificationsReportFilter));

    // Refresh start and end dates (since the actual date may have changed since the UI was last refreshed)
    refreshDateRange(newReportFilter, newReportFilter.DateRange);

    newReportFilter.Division = event.value;
    newReportFilter.SiteNumbers = [];
    newReportFilter.LaneNumbers = [];

    if (isReportsPage) {
      saveReportFilter(dispatch, newReportFilter);
    } else {
      saveNotificationsReportFilter(dispatch, newReportFilter);
    }
  };

  React.useEffect(() => {
    let uniqueStoreList = getUniqueStore(stores);
    if (!uniqueStoreList.includes(selectAll) && uniqueStoreList.length > 0) {
      uniqueStoreList.splice(0, 0, selectAll);
    }

    setStoreList(uniqueStoreList);
  }, [stores, selectedLanguage]);

  const getUniqueStore = (store: string[]) => {
    let result: string[] = [];
    for (let i = 0; i < store.length; ++i) {
      if (!result.includes(store[i])) {
        result.push(store[i]);
      }
    }
    return result;
  };

  React.useEffect(() => {
    if (reportNodeInfo.length > 0) {
      let lanesNumberData = getUniqueLaneNumbers(reportNodeInfo);

      if (!lanesNumberData.includes(selectAll) && lanesNumberData.length > 0) {
        lanesNumberData.splice(0, 0, selectAll);
      }

      setLaneNumbers(lanesNumberData);
    }
  }, [reportNodeInfo, selectedLanguage]);

  const clearLaneFilterData = () => {
    setLaneNumbers([]);
    setSelectedLanes([]);
  };

  const getUniqueLaneNumbers = (reportNodeInfo: ReportNodeInfo[]): string[] => {
    let result: string[] = [];
    for (let i = 0; i < reportNodeInfo.length; ++i) {
      if (!result.includes(reportNodeInfo[i].LaneNumber)) {
        result.push(reportNodeInfo[i].LaneNumber);
      }
    }
    return result;
  };

  useEffect(() => {
    setNotificationVisible(false);
    fetchDivisions(dispatch);
    return () => {};
  }, []);

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps,
  ) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={itemProps.selected}
          onChange={(event: any) => itemProps.onClick(itemProps.index, event)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const isDateRangeChanged = () => {
    setIsStartDateRangeChange(false);
    setIsEndDateRangeChange(false);
    setPanelBarVisibility(dispatch, true);
  };

  const onDateRangeOptionChange = (event: DropDownListChangeEvent) => {
    let dateRange: ApiEnums.DateRangeOptions | undefined;
    switch (event.value) {
      case dateRangeOption[0]:
        dateRange = ApiEnums.DateRangeOptions.Today;
        break;
      case dateRangeOption[1]:
        dateRange = ApiEnums.DateRangeOptions.Yesterday;
        break;
      case dateRangeOption[2]:
        dateRange = ApiEnums.DateRangeOptions.LastSevenDays;
        break;
      case dateRangeOption[3]:
        dateRange = ApiEnums.DateRangeOptions.Other;
        break;
    }

    if (dateRange === undefined) return;

    let newReportFilter: ReportFilter = isReportsPage
      ? JSON.parse(JSON.stringify(reportFilter))
      : JSON.parse(JSON.stringify(notificationsReportFilter));
    newReportFilter.DateRange = dateRange;

    if (dateRange === ApiEnums.DateRangeOptions.Other) {
      setModalVisible(true);
      setResetDateRangeOptions(event.value);
      setSelectedDateRange(event.value);
    } else {
      refreshDateRange(newReportFilter, dateRange);
      setResetDateRangeOptions(event.value);
      setSelectedDateRange(event.value);
      setDateRangeSelectionVisible(false);
      isDateRangeChanged();
    }

    if (isReportsPage) {
      saveReportFilter(dispatch, newReportFilter);
    } else {
      saveNotificationsReportFilter(dispatch, newReportFilter);
    }

    setPanelBarVisibility(dispatch, true);
    returnToReportsList?.();
  };

  const handleClose = () => {
    setModalVisible(false);
    setPanelBarVisibility(dispatch, true);
    if (!dateRangeSelectionVisible) {
      setResetDateRangeOptions([dateRangeOption[0]]);
    }
  };

  const cancelButtonOnClick = () => {
    setModalVisible(false);
    setPanelBarVisibility(dispatch, true);
    if (!dateRangeSelectionVisible) {
      setResetDateRangeOptions([dateRangeOption[0]]);
    }
  };

  const okButtonOnClick = () => {
    let newReportFilter = isReportsPage
      ? JSON.parse(JSON.stringify(reportFilter))
      : JSON.parse(JSON.stringify(notificationsReportFilter));

    if (startDateRange > endDateRange || startDateRange > new Date()) {
      setErrorMsg(true);
    } else {
      setModalVisible(false);
      newReportFilter.StartDateTime = startDateRange;
      newReportFilter.EndDateTime = endDateRange;
      if (isReportsPage) {
        saveReportFilter(dispatch, newReportFilter);
      } else {
        saveNotificationsReportFilter(dispatch, newReportFilter);
      }
      setDateRangeSelectionVisible(true);
      setPanelBarVisibility(dispatch, true);
      if (!isStartDateRangeChange || !isEndDateRangeChange) {
        setIsStartDateRangeChange(false);
        setIsEndDateRangeChange(false);
      }
    }
  };

  const onOpen = () => {
    if (resetDateRangeOption.toString() === dateRangeOption[3]) {
      setResetDateRangeOptions([dateRangeOption[3]]);
    }
  };

  const isDateRangeSelectionEnabled = () => {
    return selectedStores.length >= 1;
  };

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (selectedFilter.Division == undefined) return;

    if (
      divisions.includes(selectedFilter.Division) &&
      selectedDivision !== ''
    ) {
      setSelectedDivision(selectedFilter.Division);
      fetchStores(dispatch, selectedFilter.Division);
    }
  }, [divisions]);

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (selectedFilter.SiteNumbers === undefined) return;

    const existed: boolean = selectedFilter.SiteNumbers.every(value => {
      return stores.includes(value);
    });

    if (selectedFilter.SiteNumbers.length !== 0 && existed) {
      setPanelBarVisibility(dispatch, selectedFilter.SiteNumbers.length >= 1);

      setSelectedStores(selectedFilter.SiteNumbers);
    }

    if (selectedFilter.SiteNumbers.length === 1) {
      fetchLanesBySite(dispatch, selectedFilter.SiteNumbers[0]);
    }
  }, [stores]);

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (selectedFilter.LaneNumbers == undefined) return;

    const existed: boolean = selectedFilter.LaneNumbers.every(value => {
      return laneNumbers.includes(value);
    });

    if (selectedFilter.LaneNumbers.length != 0 && existed) {
      if (selectedFilter.LaneNumbers.length == laneNumbers.length - 1)
        setSelectedLanes(laneNumbers);
      else setSelectedLanes(selectedFilter.LaneNumbers);
    }
  }, [laneNumbers]);

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (
      selectedFilter.StartDateTime == undefined ||
      selectedFilter.EndDateTime == undefined
    )
      return;

    if (selectedFilter.DateRange == ApiEnums.DateRangeOptions.Today) {
      setResetDateRangeOptions([dateRangeOption[0]]);
    } else if (
      selectedFilter.DateRange == ApiEnums.DateRangeOptions.Yesterday
    ) {
      setResetDateRangeOptions([dateRangeOption[1]]);
    } else if (
      selectedFilter.DateRange == ApiEnums.DateRangeOptions.LastSevenDays
    ) {
      setResetDateRangeOptions([dateRangeOption[2]]);
    } else {
      setResetDateRangeOptions([dateRangeOption[3]]);
      setDateRangeSelectionVisible(true);
    }
  }, [dateRangeSelection, selectedLanguage]);

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (selectedFilter.StartDateTime == undefined) return;
    let prevStartDateRange = new Date(selectedFilter.StartDateTime);
    setStartDateRange(prevStartDateRange);
  }, [selectedStartDateTime]);

  React.useEffect(() => {
    const selectedFilter: ReportFilter = isReportsPage
      ? reportFilter
      : notificationsReportFilter;

    if (selectedFilter.EndDateTime == undefined) return;
    let prevEndDateRange = new Date(selectedFilter.EndDateTime);
    setEndDateRange(prevEndDateRange);
  }, [selectedEndDateTime]);

  React.useEffect(() => {
    setIsReportsPanelBarVisible(panelBarIsVisible);
  }, [panelBarIsVisible]);

  React.useEffect(() => {
    if (
      reportFilter.SiteNumbers.length >= 1 &&
      laneSelectionChange !== undefined
    )
      setPanelBarVisibility(dispatch, true);
  }, [reportFilter]);

  useEffect(() => {
    const filter = isReportsPage ? reportFilter : notificationsReportFilter;
    setSelectedDivision(filter.Division);
    setSelectedStores(filter.SiteNumbers);
    setSelectedLanes(filter.LaneNumbers);
    setSelectedStartDateTime(filter.StartDateTime);
    setSelectedEndDateTime(filter.EndDateTime);
    if (filter.DateRange === 4) {
      setSelectedDateRange(dateRangeOption[3]);
      setDateRangeSelectionVisible(true);
    } else {
      setSelectedDateRange(dateRangeOption[filter.DateRange]);
    }
  }, [reportFilter, notificationsReportFilter]);

  const getItemsLength = (data: string[]) => {
    return data.filter(item => item !== selectAll).length;
  };

  const selectedStoreCount = getItemsLength(selectedStores);
  const selectedLaneCount = getItemsLength(selectedLanes);

  return (
    <React.Fragment>
      <div className={classes.mainContainer}>
        <div className={classes.reportFilterContainer}>
          <Grid
            container
            spacing={1}
            className={clsx(classes.gridAlign, classes.margin)}
          >
            <Grid item className={classes.dropDownLabelGrid}>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'DIVISION'}
                gutterBottom={true}
              />
              <DynamicDropDownList
                data={divisions}
                width={'270px'}
                onChange={handleDivisionChange}
                className={classes.enable}
                value={selectedDivision}
                loading={isFetchingDivisions}
              />
            </Grid>

            {isReportsPage && (
              <Grid className={classes.iconGrid}>
                <img
                  src={selectedDivision == undefined ? disableIcon : enableIcon}
                  className={classes.arrowIcon}
                />
              </Grid>
            )}

            <Grid item className={classes.dropDownLabelGrid}>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'STORES'}
                gutterBottom={true}
              />
              <MultiSelect
                data={storeList}
                itemRender={itemRender}
                value={selectedStores}
                autoClose={false}
                disabled={!selectedDivision}
                onChange={onStoreChange}
                className={!selectedDivision ? classes.disable : classes.enable}
                tags={
                  selectedStores.length > 0
                    ? [
                        {
                          text: `${selectedStoreCount} ${selectedStoreCount === 1 ? storeSelected : storesSelected} ${itemSelected}`,
                          data: [...selectedStores],
                        },
                      ]
                    : []
                }
                loading={isFetchingStores}
              />
            </Grid>
            {reportFilter.SiteNumbers.length == 1 && isReportsPage ? (
              <Grid className={classes.iconGrid}>
                <img
                  src={selectedStores.length == 0 ? disableIcon : enableIcon}
                  className={classes.arrowIcon}
                />
              </Grid>
            ) : null}
            {(isReportsPage
              ? reportFilter.SiteNumbers.length
              : notificationsReportFilter.SiteNumbers.length) === 1 ? (
              <Grid item className={classes.dropDownLabelGrid}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'LANES'}
                  gutterBottom={true}
                />
                <MultiSelect
                  data={laneNumbers}
                  itemRender={itemRender}
                  value={selectedLanes}
                  autoClose={false}
                  onChange={onLaneChange}
                  className={classes.enable}
                  tags={
                    selectedLanes.length > 0
                      ? [
                          {
                              text: `${selectedLaneCount} ${selectedLaneCount === 1 ? laneSelected : lanesSelected} ${itemSelected}`,
                              data: [...selectedLanes],
                          },
                        ]
                      : []
                  }
                  loading={isFetchingReportNodeInfo}
                />
              </Grid>
            ) : null}
            {isReportsPage && (
              <Grid className={classes.iconGrid}>
                <img
                  src={isDateRangeSelectionEnabled() ? enableIcon : disableIcon}
                  className={classes.arrowIcon}
                />
              </Grid>
            )}
            <Grid item className={classes.dateRangeDropDownLabelGrid}>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'DATE_RANGE'}
                gutterBottom={true}
              />
              <DynamicDropDownList
                data={dateRangeOption}
                disabled={isDateRangeSelectionEnabled() ? false : true}
                onChange={onDateRangeOptionChange}
                width={'270px'}
                value={isReportsPage ? resetDateRangeOption : selectedDateRange}
                className={
                  isDateRangeSelectionEnabled()
                    ? classes.enable
                    : classes.disable
                }
                onOpen={onOpen}
              />
              {dateRangeSelectionVisible ? (
                <div className={classes.selectedDateRange}>
                  <StringResourceManager
                    stringKey={'START_DATE_TIME'}
                    style="startDateRange"
                    label={startDateRange.toLocaleString()}
                  />
                  <StringResourceManager
                    stringKey={'END_DATE_TIME'}
                    style="endDateRange"
                    label={endDateRange.toLocaleString()}
                  />
                </div>
              ) : null}
            </Grid>
            {modalVisible ? (
              <Dialog
                title={<StringResourceManager stringKey={'DATE_RANGE'} />}
                onClose={handleClose}
                className={classes.dateRangeModal}
              >
                <div className={classes.dateRangeContainer}>
                  <div className={classes.headerRowElement}>
                    <DateTimePicker
                      value={startDateRange}
                      onChange={event => {
                        setStartDateRange(event.value!);
                        setIsStartDateRangeChange(true);
                        setErrorMsg(false);
                      }}
                      label={startDateLabel}
                    />
                    {errorMsg && (
                      <Error direction={'start'}>{START_DATE_ERRORMSG}</Error>
                    )}
                  </div>
                  <div className={classes.headerRowElement}>
                    <DateTimePicker
                      value={endDateRange}
                      label={endDateLabel}
                      onChange={event => {
                        setEndDateRange(event.value!);
                        setIsEndDateRangeChange(true);
                        setErrorMsg(false);
                      }}
                    />
                    {errorMsg && (
                      <Error direction={'start'}>{END_DATE_ERRORMSG}</Error>
                    )}
                  </div>
                </div>
                <DialogActionsBar layout={'stretched'}>
                  <Button
                    onClick={okButtonOnClick}
                    className={classes.actionButton}
                  >
                    <StringResourceManager stringKey={'OK_BUTTON'} />
                  </Button>
                  <Button
                    onClick={cancelButtonOnClick}
                    className={classes.actionButton}
                  >
                    <StringResourceManager stringKey={'CANCEL_BUTTON'} />
                  </Button>
                </DialogActionsBar>
              </Dialog>
            ) : null}
          </Grid>
        </div>
      </div>
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {displayNotification && (
            <Notification
              className={classes.notificationText}
              type={{
                style: reportExportResult.Code == 1 ? 'success' : 'error',
                icon: true,
              }}
            >
              {reportExportResult.Message}
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
      <Divider />
    </React.Fragment>
  );
};

export default ReportsFilter;
